/**
 * Repository roles and relations (YAG-1067): what each workspace repository is
 * to the others, and how they relate. The value sets live here rather than in
 * CHECKs (as `repoCapabilities.ts` keeps its own), so a new kind ships without
 * a migration.
 *
 * `repo_index.relation_facts` holds what one repository's own reading says
 * about itself (names and paths only, never a value or file text); the relate
 * step derives `repo_relations` edges from every repository's facts.
 */
import { z } from "zod";
/** What a repository is to the others. A repository with no index row has no role. */
export const REPO_ROLES = [
    "application",
    "library",
    "environment",
    "documentation",
    "archive",
];
export const repoRoleSchema = z.enum(REPO_ROLES);
export const REPO_ROLE_SOURCES = ["inferred", "person"];
/** The most a role's reason may say. */
export const REPO_ROLE_REASON_MAX = 120;
/** Each kind reads in one direction: from the environment, the docs, or the consumer. */
export const REPO_RELATION_KINDS = ["defines_environment_for", "documents", "depends_on"];
export const repoRelationKindSchema = z.enum(REPO_RELATION_KINDS);
/** Where an edge came from, weakest first: a person's row beats both others. */
export const REPO_RELATION_SOURCES = ["inferred", "customer_instructions", "person"];
export const repoRelationSourceSchema = z.enum(REPO_RELATION_SOURCES);
/** A source's strength: higher wins. */
export function relationSourceRank(source) {
    return REPO_RELATION_SOURCES.indexOf(source);
}
/** How sure `repo.route` is of the repository it named (`RepoSuggestion.confidence`). */
export const REPO_ROUTE_CONFIDENCES = ["high", "medium", "low"];
/**
 * How a ticket's repository was chosen, as `tickets.source_ref.repoChoice.by`
 * records it: the Team's best estimate (`repo.route`, a person never chose
 * it), or moved there because the plan found the change belongs there. A
 * ticket with a repository and no such mark had it set by a person or by the
 * Team having only one.
 */
export const REPO_CHOICE_BEST_ESTIMATE = "best_estimate";
export const REPO_CHOICE_PLAN_MOVED = "plan_moved";
/**
 * Whether the ticket's repository is the Team's guess rather than a person's
 * choice: its `repoChoice` mark is a best estimate (or a plan's move) of the
 * very repository the ticket is on now.
 */
export function repoChosenByTeam(sourceRef, repoId) {
    return repoChoiceBy(sourceRef, repoId) !== null;
}
/**
 * How the Team chose the ticket's current repository, or null when it did
 * not (a person did, or nothing is recorded). A plan moves a ticket on its
 * own only off a best estimate, never off its own earlier move, so two plans
 * can never hand a ticket back and forth.
 */
export function repoChoiceBy(sourceRef, repoId) {
    if (!repoId || !sourceRef || typeof sourceRef !== "object")
        return null;
    const choice = sourceRef.repoChoice;
    if (!choice || typeof choice !== "object")
        return null;
    const { by, repoId: chosen } = choice;
    if (typeof chosen !== "string" || chosen.toLowerCase() !== repoId.toLowerCase())
        return null;
    return by === REPO_CHOICE_BEST_ESTIMATE || by === REPO_CHOICE_PLAN_MOVED ? by : null;
}
/** Compose files a repository's facts keep, shallowest first. */
export const RELATION_FACTS_COMPOSE_FILES_MAX = 4;
/** Services kept per compose file. */
export const RELATION_FACTS_SERVICES_MAX = 60;
export const RELATION_FACTS_POINTERS_MAX = 40;
export const RELATION_FACTS_PAGES_MAX = 10;
const name = z.string().min(1).max(300);
const path = z.string().min(1).max(500);
/** One compose service as the facts keep it: its identity, never its values. */
export const composeServiceIdentitySchema = z
    .object({
    key: name,
    /** The last segment of its build context (`../gpx-engine` is `gpx-engine`). */
    contextName: name.nullable(),
    /** The build context leaves the compose file's own repository. */
    contextLeaves: z.boolean(),
    containerName: name.nullable(),
    /** The image's name without registry, tag or digest. */
    imageBase: name.nullable(),
    /** A known infrastructure image (MySQL, Redis, ...). */
    infrastructure: z.boolean(),
})
    .strict();
export const repoRelationFactsSchema = z
    .object({
    version: z.union([z.literal(1), z.literal(2)]),
    /** The head these facts were read at; null when the read failed. */
    sha: z.string().max(64).nullable(),
    readAt: z.string().datetime(),
    /** A typed cause code when the read failed; the lists are then empty. */
    error: z.string().min(1).max(100).optional(),
    compose: z
        .array(z
        .object({
        file: path,
        services: z.array(composeServiceIdentitySchema).max(RELATION_FACTS_SERVICES_MAX),
    })
        .strict())
        .max(RELATION_FACTS_COMPOSE_FILES_MAX),
    pointers: z
        .array(z
        .object({
        file: path,
        line: z.number().int().positive(),
        repository: name,
        hint: repoRelationKindSchema.nullable(),
    })
        .strict())
        .max(RELATION_FACTS_POINTERS_MAX)
        .optional(),
    pages: z
        .array(z
        .object({
        repository: name,
        pages: z.array(path).max(RELATION_FACTS_PAGES_MAX),
        mentions: z.number().int().nonnegative(),
    })
        .strict())
        .optional(),
})
    .strict();
/**
 * A person's word on one edge: `asserts` it, `denies` it (the index cannot
 * bring it back), or `null` removes the person's row so the index decides
 * again. `direction` is which end the other repository is, as
 * {@link EffectiveRepoRelation.otherIs}: `from` documents, defines the
 * services of, or uses this one.
 */
export const repoRelationWriteSchema = z
    .object({
    otherRepoId: z.string().uuid(),
    kind: repoRelationKindSchema,
    direction: z.enum(["from", "to"]),
    stance: z.enum(["asserts", "denies"]).nullable(),
})
    .strict();
//# sourceMappingURL=repoRelations.js.map